const MICROSOFT_CLICK_ID_KEY = 'julius_msclkid';
const MICROSOFT_CLICK_ID_TTL_MS = 90 * 24 * 60 * 60 * 1000;

type StoredMicrosoftClickId = {
  value: string;
  expiresAt: number;
};

type MicrosoftUetEventProperties = {
  eventCategory?: string | null;
  eventLabel?: string | null;
  value?: number | null;
  currency?: string | null;
  transactionId?: string | null;
  properties?: Record<string, unknown>;
};

const compact = (properties: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );

const getStorage = () => {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.localStorage;
  } catch (_error) {
    return undefined;
  }
};

const parseStoredClickId = (value: string | null): StoredMicrosoftClickId | undefined => {
  if (!value) return undefined;
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed?.value !== 'string' || typeof parsed?.expiresAt !== 'number') return undefined;
    return parsed;
  } catch (_error) {
    return undefined;
  }
};

export const captureMicrosoftClickIdFromUrl = (url = typeof window !== 'undefined' ? window.location.href : '') => {
  if (!url) return undefined;

  let msclkid: string | null = null;
  try {
    msclkid = new URL(url).searchParams.get('msclkid');
  } catch (_error) {
    return undefined;
  }

  if (!msclkid) return undefined;

  const storage = getStorage();
  if (!storage) return msclkid;

  storage.setItem(
    MICROSOFT_CLICK_ID_KEY,
    JSON.stringify({
      value: msclkid,
      expiresAt: Date.now() + MICROSOFT_CLICK_ID_TTL_MS
    })
  );

  return msclkid;
};

export const getStoredMicrosoftClickId = () => {
  const storage = getStorage();
  if (!storage) return undefined;

  const storedClickId = parseStoredClickId(storage.getItem(MICROSOFT_CLICK_ID_KEY));
  if (!storedClickId) return undefined;

  if (storedClickId.expiresAt <= Date.now()) {
    storage.removeItem(MICROSOFT_CLICK_ID_KEY);
    return undefined;
  }

  return storedClickId.value;
};

export const getMicrosoftClickId = () => captureMicrosoftClickIdFromUrl() || getStoredMicrosoftClickId();

export const trackMicrosoftUetEvent = (
  action: string,
  { eventCategory, eventLabel, value, currency, transactionId, properties = {} }: MicrosoftUetEventProperties = {}
) => {
  if (typeof window === 'undefined') return;
  window.uetq = window.uetq || [];

  const payload = compact({
    event_category: eventCategory,
    event_label: eventLabel,
    event_value: value,
    revenue_value: value,
    currency,
    transaction_id: transactionId,
    ...properties
  });

  window.uetq.push('event', action, payload);
};
